// client/src/pages/SignIn.js
import {
  Box, Button, Flex, FormControl, FormLabel, HStack, Image, Input,
  Link, Modal, ModalContent, ModalOverlay, PinInput, PinInputField,
  Text, VStack, useToast, useDisclosure,
} from '@chakra-ui/react';
import { FaGoogle, FaApple, FaFacebookF } from 'react-icons/fa';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from "react-router-dom";
import { signOut } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import TermsDialog from '../components/TermsDialog';
import PrivacyDialog from '../components/PrivacyDialog';

const RESEND_COOLDOWN = 60;

export default function SignIn() {
  const {
    user,
    authLoading,
    requestOTP,
    verifyOTP,
    loginWithGoogle,
    googleSigningIn,
  } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const { isOpen: isTermsOpen, onOpen: onTermsOpen, onClose: onTermsClose } = useDisclosure();
  const { isOpen: isPrivacyOpen, onOpen: onPrivacyOpen, onClose: onPrivacyClose } = useDisclosure();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) navigate('/', { replace: true });
  }, [user, authLoading, navigate]);

  // Restore email on page refresh mid-OTP (session reset — go back to email step)
  useEffect(() => {
    const saved = sessionStorage.getItem('otp_pending_email');
    if (saved) {
      setEmail(saved);
      // Session is gone after refresh — stay on email step, don't auto-open OTP
      toast({
        title: 'Session reset',
        description: 'Please re-enter your email to get a new code.',
        status: 'info', duration: 4000, isClosable: true, position: 'top',
      });
      sessionStorage.removeItem('otp_pending_email');
    }
  }, [toast]);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // ── Step 1: Send OTP ─────────────────────────────────────────
  const handleSendOTP = async (e) => {
    e?.preventDefault();
    setEmailError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    try {
      await requestOTP(email);
      sessionStorage.setItem('otp_pending_email', email);
      setStep('otp');
      startCooldown();
    } catch (err) {
      if (err.code === 'WRONG_AUTH_METHOD') return;
      setEmailError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: Verify OTP ───────────────────────────────────────
  const handleVerifyOTP = async (completedVal) => {
    const code = typeof completedVal === 'string' ? completedVal : otp;
    if (code.length !== 8) { setOtpError('Please enter the full 8-digit code.'); return; }
    setOtpError('');
    setIsLoading(true);
    try {
      const userData = await verifyOTP(code);
      sessionStorage.removeItem('otp_pending_email');
      if (userData?.conflict) {
        setStep('email');
        setOtp('');
        return;
      }
      if (userData) {
        navigate(userData.onboardingCompleted ? '/' : '/?onboarding=true', { replace: true });
      }
    } catch (err) {
      if (err.code === 'WRONG_AUTH_METHOD') {
        setStep('email');
        setOtp('');
        sessionStorage.removeItem('otp_pending_email');
        return;
      }
      if (err.name === 'CodeMismatchException' || err.message?.toLowerCase().includes('code')) {
        setOtpError('Incorrect code. Please check and try again.');
      } else if (
        (err.name === 'NotAuthorizedException' && err.message?.toLowerCase().includes('session')) ||
        err.message?.toLowerCase().includes('expired')
      ) {
        setOtpError('Session expired. Returning to sign in...');
        setTimeout(async () => {
          try { await signOut(); } catch { }
          setStep('email');
          setOtp('');
          setOtpError('');
          clearInterval(cooldownRef.current);
          setCooldown(0);
        }, 3000);
      } else if (err.name === 'TooManyRequestsException' || err.name === 'LimitExceededException') {
        setOtpError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setOtpError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend OTP ───────────────────────────────────────────────
  const handleResend = async () => {
    if (cooldown > 0 || isLoading) return;
    setOtpError('');
    setOtp('');
    setIsLoading(true);
    try {
      // Cancel any existing Amplify sign-in state before re-initiating
      try { await signOut(); } catch { }
      await requestOTP(email);
      startCooldown();
      toast({
        title: 'New code sent',
        description: `A new OTP was sent to ${email}.`,
        status: 'success', duration: 4000, isClosable: true, position: 'top',
      });
    } catch (err) {
      if (err.code === 'WRONG_AUTH_METHOD') return;
      if (err.name === 'LimitExceededException' || err.name === 'TooManyRequestsException') {
        setOtpError('Too many requests. Please wait a few minutes.');
      } else {
        setOtpError(err.message || 'Could not resend. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const userData = await loginWithGoogle();
      if (userData?.conflict) return;
      if (userData) {
        navigate(userData.onboardingCompleted ? '/' : '/?onboarding=true', { replace: true });
      }
    } catch (err) {
      /* toast shown in AuthContext */
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) return null;

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      // justifyContent="center"
      px="clamp(20px, 4vw, 48px)"
      py="clamp(24px, 4vh, 48px)"
      boxSizing="border-box"
      transition="margin-left 0.25s ease"
      border="1px solid #ECECEC"
      borderRadius="20px"
      bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
    >
      <VStack
        w="100%"
        maxW="clamp(320px, 90vw, 420px)"
        spacing="clamp(20px, 2.5vw, 28px)"
        // spacing="clamp(100px, 50vw, 150px)" //no social login spacing
        align="stretch"
      >
        {/* Logo */}
        <Flex justify="center"
        // mb="clamp(8px, 1vh, 16px)"
        >
          <Link as={RouterLink} to="/">
            <Image
              src="/logoSignUp.svg"
              alt="Alpherra"
              h="clamp(48px, 6vw, 72px)"
              objectFit="contain"
            />
          </Link>
        </Flex>

        {/* Heading */}
        <VStack
        // mb="clamp(8px, 1vh, 16px)"
        >
          {/* <Text
            fontSize="clamp(12px, 1.1vw, 20px)"
            fontWeight="semibold"
            bgClip="text"
            textAlign="center"
            lineHeight="1.25"
            mx="auto"
            maxW="520px"
            bgGradient="linear(to-r, #d414a3 0%, #8b5cf6 65%, #00d4ff 100%)"
          >
            Build smarter <br /> Where engineering meets intelligence
          </Text> */}
          <Text
            fontSize="clamp(22px, 1.75vw, 36px)"
            fontWeight="semibold"
            color="#242424"
            textAlign="center"
            lineHeight="1.25"
          >
            Log in or Sign up
          </Text>
        </VStack>

        {/* Social sign-in buttons */}
        <VStack spacing="clamp(10px, 1vw, 14px)" w="100%" align="stretch">
          {/* Google */}
          <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #DADADA"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={
              <Box
                as="span"
                display="inline-block"
                w="clamp(18px, 1.3vw, 20px)"
                h="clamp(18px, 1.3vw, 20px)"
              >
                <svg viewBox="0 0 48 48" width="100%" height="100%">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.9-6.9C35.64 2.54 30.2 0 24 0 14.64 0 6.48 5.38 2.56 13.22l8.06 6.26C12.5 13.38 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.1 24.55c0-1.64-.15-3.22-.42-4.73H24v9.01h12.4c-.54 2.91-2.18 5.37-4.66 7.02l7.18 5.58C43.96 37.4 46.1 31.51 46.1 24.55z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.62 28.48A14.5 14.5 0 0 1 9.5 24c0-1.56.27-3.07.76-4.48l-8.06-6.26A23.98 23.98 0 0 0 0 24c0 3.77.9 7.34 2.2 10.26l8.42-5.78z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.2 0 11.4-2.05 15.2-5.57l-7.18-5.58c-2 1.34-4.57 2.13-8.02 2.13-6.26 0-11.5-3.88-13.38-9.48l-8.42 5.78C6.48 42.62 14.64 48 24 48z"
                  />
                </svg>
              </Box>
            }
            onClick={handleGoogleSignIn}
            isDisabled={googleSigningIn || isLoading}
          >
            Sign in with Google
          </Button>

          {/* Apple */}
          {/* <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #DADADA"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={<Box as={FaApple} fontSize="clamp(18px, 1.3vw, 22px)" />}
          >
            Sign in with Apple
          </Button> */}

          {/* Facebook */}
          {/* <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #DADADA"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={
              <Box
                as={FaFacebookF}
                color="#1877F2"
                fontSize="clamp(18px, 1.3vw, 22px)"
              />
            }
          >
            Sign in with Facebook
          </Button> */}
        </VStack>

        {/* OR separator */}
        <Flex align="center" w="100%" gap="clamp(12px, 1.5vw, 20px)">
          <Box flex={1} h="1px" bg="#E0E0E0" />
          <Text
            fontSize="clamp(13px, 1vw, 15px)"
            color="#6D6D6D"
            fontWeight="medium"
          >
            OR
          </Text>
          <Box flex={1} h="1px" bg="#E0E0E0" />
        </Flex>

        {/* Form */}
        <VStack
          as="form"
          onSubmit={handleSendOTP}
          spacing="clamp(16px, 1.5vw, 20px)"
          align="stretch"
          border="1px solid #DADADA"
          borderRadius="20px"
          padding='10px'
        >
          {/* Email */}
          <FormControl isRequired isInvalid={!!emailError}>
            <FormLabel
              fontSize="clamp(14px, 1vw, 16px)"
              fontWeight="semibold"
              color="#4F4F4F"
              mb="clamp(6px, 0.5vw, 8px)"
            >
              Email address
            </FormLabel>
            <Input
              name="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(''); }}
              size="md"
              h="clamp(44px, 4vh, 52px)"
              borderRadius="12px"
              borderColor={emailError ? 'red.400' : '#DADADA'}
              // border="1px solid #DADADA"
              _placeholder={{ color: "#9E9E9E" }}
              _hover={{ borderColor: "#D0D0D0" }}
              _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
            />
            {emailError && (
              <Text fontSize="xs" color="red.500" mt={1}>{emailError}</Text>
            )}
          </FormControl>

          {/* Password with error state */}
          {/* <FormControl isRequired>
            <FormLabel
              fontSize="clamp(14px, 1vw, 16px)"
              fontWeight="semibold"
              color="#4F4F4F"
              mb="clamp(6px, 0.5vw, 8px)"
            >
              Password
            </FormLabel>
            <InputGroup size="lg">
              <Input
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                h="clamp(44px, 4vh, 52px)"
                borderRadius="12px"
                border={
                  hasError ? "1px solid #FF7373" : "1px solid #DADADA"
                }
                boxShadow={
                  hasError
                    ? "0 3px 4px -3px rgba(0,0,0,0.16), 0 0 0 3px #FFE0E0"
                    : "none"
                }
                _placeholder={{ color: hasError ? "#DE2424" : "#9E9E9E" }}
                _hover={{
                  borderColor: hasError ? "#FF7373" : "#D0D0D0",
                }}
                _focus={{
                  borderColor: hasError ? "#FF7373" : "#CF008A",
                  boxShadow: hasError
                    ? "0 3px 4px -3px rgba(0,0,0,0.16), 0 0 0 3px #FFE0E0"
                    : "0 0 0 1px #CF008A",
                }}
              />
              <InputRightElement h="clamp(44px, 4vh, 52px)">
                <Button
                  variant="ghost"
                  size="sm"
                  fontSize="clamp(12px, 0.9vw, 14px)"
                  color="#6D6D6D"
                  _hover={{ bg: "#F5F5F5" }}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </InputRightElement>
            </InputGroup>

            {hasError && (
              <Flex
                mt="6px"
                align="center"
                gap="6px"
                pl="2px"
              >
                <WarningIcon boxSize="16px" color="#DE2424" />
                <Text
                  fontSize="12px"
                  fontWeight="semibold"
                  color="#DE2424"
                >
                  Password is incorrect. Please check and try again.
                </Text>
              </Flex>
            )}

            {showResendVerification && (
              <Text fontSize="sm" color="blue.500" cursor="pointer" onClick={handleResendVerification}>
                Resend verification email
              </Text>
            )}
          </FormControl> */}

          {/* Remember + Forgot password row */}
          {/* <Flex align="center" justify="space-between">

            <Checkbox
              colorScheme="pink"
              size="md"
              color="#6D6D6D"
              fontSize="clamp(13px, 1vw, 15px)"
              sx={{
                "& .chakra-checkbox__control": { borderColor: "#C0C0C0" },
                "& .chakra-checkbox__control[data-checked]": { borderColor: "#CF008A" },
              }}
              defaultChecked
            >
              Remember me for 30 days
            </Checkbox>

            <Link
              as="button"
              type="button"
              fontSize="clamp(13px, 1vw, 15px)"
              color="#CF008A"
              onClick={onForgotPasswordOpen}
            >
              Forgot password?
            </Link>
          </Flex> */}

          {/* Sign in button + policy text */}
          <VStack spacing={0} align="stretch" w="100%">
            <Button
              type="submit"
              isLoading={isLoading}
              loadingText="Signing in..."
              bg="#CF008A"
              color="white"
              size="lg"
              h="clamp(48px, 4.5vh, 56px)"
              fontSize="clamp(15px, 1.1vw, 17px)"
              fontWeight="semibold"
              borderRadius="12px"
              _hover={{ bg: "#D933A1" }}
              _active={{ bg: "#B8007A" }}
              mb="clamp(4px, 0.4vw, 6px)"
            >
              Continue with Email
            </Button>

            <Text
              fontSize="clamp(12px, 0.85vw, 14px)"
              color="#4F4F4F"
              fontWeight="medium"
              textAlign="center"
              lineHeight="1.5"
              sx={{ textWrap: 'balance' }}
            >
              By signing in, you agree to our{" "}
              <Link
                color="#CF008A"
                textDecoration="underline"
                whiteSpace="nowrap"
                cursor="pointer"
                _hover={{ color: "#D933A1" }}
                onClick={onTermsOpen}
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                color="#CF008A"
                textDecoration="underline"
                whiteSpace="nowrap"
                cursor="pointer"
                _hover={{ color: "#D933A1" }}
                onClick={onPrivacyOpen}
              >
                Privacy Policy
              </Link>
              .
            </Text>
          </VStack>
        </VStack>

        {/* Sign up link */}
        {/* <Flex
          align="center"
          justify="center"
          gap="8px"
        >
          <Text
            fontSize="clamp(16px, 1vw, 18px)"
            color="#6D6D6D"
          >
            Don’t have an account?
          </Text>
          <Link
            as={RouterLink}
            to="/signup"
            fontSize="clamp(16px, 1vw, 18px)"
            color="#CF008A"
          >
            Register
          </Link>
        </Flex> */}
      </VStack>

      {/* <ForgotPasswordDialog isOpen={isForgotPasswordOpen} onClose={onForgotPasswordClose} /> */}

      {/* ── OTP Overlay Modal ─────────────────────────────────── */}
      <Modal isOpen={step === 'otp'} onClose={() => { }} closeOnOverlayClick={false} isCentered>
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" p={6} maxW="380px" mx={4}>
          <VStack spacing={5} align="stretch">

            {/* Header */}
            <Box>
              <Text fontSize="xl" fontWeight="700" color="#1A1A1A">Check your email</Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                We sent a 8-digit code to{' '}
                <Text as="span" fontWeight="600" color="#1A1A1A">{email}</Text>
              </Text>
            </Box>

            {/* PIN Input */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="500" color="#1A1A1A">Verification code</FormLabel>
              <HStack justify="center" spacing={2}>
                <PinInput
                  otp
                  size="lg"
                  value={otp}
                  onChange={val => { setOtp(val); setOtpError(''); }}
                  onComplete={handleVerifyOTP}
                  isDisabled={isLoading}
                  focusBorderColor="#CF008A"
                >
                  {[...Array(8)].map((_, i) => (
                    <PinInputField
                      key={i}
                      borderRadius="10px"
                      borderColor={otpError ? 'red.400' : '#E2E8F0'}
                      fontSize="xl"
                    />
                  ))}
                </PinInput>
              </HStack>

              {otpError && (
                <Text fontSize="xs" color={otpError.includes('expired') ? 'orange.500' : 'red.500'} mt={2} textAlign="center">
                  {otpError}
                </Text>
              )}
            </FormControl>

            {/* Verify button */}
            <Button
              w="full"
              bg="#CF008A"
              color="white"
              borderRadius="10px"
              isLoading={isLoading}
              loadingText="Verifying..."
              onClick={handleVerifyOTP}
              _hover={{ bg: '#b0007a' }}
              isDisabled={otp.length !== 8}
            >
              Verify & Sign in
            </Button>

            {/* Resend + change email */}
            <VStack spacing={1}>
              <HStack fontSize="sm" color="gray.500" justify="center">
                <Text>Didn't receive it?</Text>
                <Button
                  variant="link"
                  size="sm"
                  color={cooldown > 0 ? 'gray.400' : '#CF008A'}
                  isDisabled={cooldown > 0 || isLoading}
                  onClick={handleResend}
                  fontWeight="500"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </Button>
              </HStack>
              <Button
                variant="link"
                size="s"
                color="gray.400"
                fontWeight="400"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setOtpError('');
                  clearInterval(cooldownRef.current);
                  setCooldown(0);
                  sessionStorage.removeItem('otp_pending_email');
                }}
              >
                Use a different email
              </Button>
            </VStack>

            {/* <Text fontSize="xs" color="gray.400" textAlign="center">
              Codes expire after 10 minutes.
            </Text> */}
          </VStack>
        </ModalContent>
      </Modal>

      <TermsDialog isOpen={isTermsOpen} onClose={onTermsClose} />
      <PrivacyDialog isOpen={isPrivacyOpen} onClose={onPrivacyClose} />
    </Box>
  );
}

